import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { Portal } from '@/components/shared/Portal';
import { RoleEmblem } from '@/components/shared/RoleEmblem';
import { eventRoutes } from '@/data/routes';
import { getPlayerSheetData } from '@/features/players/selectors';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { updatePlayerPresentation } from '@/lib/repositories/adminRepository';
import { formatDateTime } from '@/utils/date';
import { formatAppRoleLabel, getPrimaryRole } from '@/utils/roles';

interface PlayerDetailSheetProps {
  profileId: string | null;
  open: boolean;
  onClose: () => void;
}

function getInitials(value: string): string {
  return value
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.slice(0, 1).toUpperCase())
    .join('');
}

function getUsageWidth(count: number, maxCount: number): string {
  if (maxCount <= 0) {
    return '0%';
  }

  return `${Math.max(12, Math.round((count / maxCount) * 100))}%`;
}

export function PlayerDetailSheet({ profileId, open, onClose }: PlayerDetailSheetProps): JSX.Element | null {
  const { profile, hasAnyRole, isApproved, needsOnboarding, refreshProfile } = useAuth();
  const { state, refreshData, isLoading, error: guildDataError } = useGuildData();
  const playerData = useMemo(() => (open && profileId ? getPlayerSheetData(state, profileId) : null), [open, profileId, state]);
  const [progressionLevelOverride, setProgressionLevelOverride] = useState('');
  const [chatMuted, setChatMuted] = useState(false);
  const [chatMutedReason, setChatMutedReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const canAdminManagePlayer = hasAnyRole(['owner', 'admin']) && Boolean(playerData);
  const canViewPlayerStats = isApproved && !needsOnboarding;
  const canSeeModerationState = hasAnyRole(['owner', 'admin', 'officer', 'mass_manager']) || profile?.id === playerData?.profile.id;
  const maxWeaponUsage = playerData ? Math.max(...playerData.weaponUsage.map((entry) => entry.usageCount), 0) : 0;
  const maxRoleUsage = playerData ? Math.max(...playerData.roleUsage.map((entry) => entry.usageCount), 0) : 0;
  const topWeapon = playerData?.weaponUsage[0];
  const topRole = playerData?.roleUsage[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    setNotice(null);
    setError(null);
    setProgressionLevelOverride(playerData?.profile.progressionLevelOverride ? String(playerData.profile.progressionLevelOverride) : '');
    setChatMuted(Boolean(playerData?.profile.chatMuted));
    setChatMutedReason(playerData?.profile.chatMutedReason ?? '');
  }, [playerData?.profile.chatMuted, playerData?.profile.chatMutedReason, playerData?.profile.progressionLevelOverride, profileId]);

  if (!open) {
    return null;
  }

  if (isLoading && profileId && !playerData) {
    return (
      <Portal>
        <div className="player-sheet" role="dialog" aria-modal="true" aria-labelledby="player-sheet-title">
          <button type="button" className="player-sheet__backdrop" aria-label="Close player sheet" onClick={onClose} />
          <aside className="player-sheet__panel">
            <header className="player-sheet__header">
              <div className="player-sheet__identity-copy">
                <p className="guild-chat__eyebrow">PLAYER PROFILE</p>
                <h3 id="player-sheet-title">Loading player profile</h3>
                <p>Refreshing attendance history, guild identity, and progression data.</p>
              </div>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </header>
          </aside>
        </div>
      </Portal>
    );
  }

  if (!playerData && guildDataError) {
    return (
      <Portal>
        <div className="player-sheet" role="dialog" aria-modal="true" aria-labelledby="player-sheet-title">
          <button type="button" className="player-sheet__backdrop" aria-label="Close player sheet" onClick={onClose} />
          <aside className="player-sheet__panel">
            <header className="player-sheet__header">
              <div className="player-sheet__identity-copy">
                <p className="guild-chat__eyebrow">PLAYER PROFILE</p>
                <h3 id="player-sheet-title">Unable to load this player</h3>
                <p>{guildDataError}</p>
              </div>
              <div className="panel-actions">
                <Button variant="secondary" onClick={() => void refreshData()}>
                  Retry
                </Button>
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
              </div>
            </header>
          </aside>
        </div>
      </Portal>
    );
  }

  if (!playerData) {
    return (
      <Portal>
        <div className="player-sheet" role="dialog" aria-modal="true" aria-labelledby="player-sheet-title">
          <button type="button" className="player-sheet__backdrop" aria-label="Close player sheet" onClick={onClose} />
          <aside className="player-sheet__panel">
            <header className="player-sheet__header">
              <div className="player-sheet__identity-copy">
                <p className="guild-chat__eyebrow">PLAYER PROFILE</p>
                <h3 id="player-sheet-title">Player not found</h3>
                <p>This player could not be loaded from the current guild state.</p>
              </div>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </header>
          </aside>
        </div>
      </Portal>
    );
  }

  const handleSave = async () => {
    if (!canAdminManagePlayer) {
      return;
    }

    setIsSaving(true);
    setNotice(null);
    setError(null);

    try {
      const nextLevelOverride = progressionLevelOverride.trim() ? Number(progressionLevelOverride) : null;

      await updatePlayerPresentation({
        profileId: playerData.profile.id,
        progressionLevelOverride:
          nextLevelOverride === null ? null : Number.isFinite(nextLevelOverride) ? nextLevelOverride : null,
        chatMuted,
        chatMutedReason,
      });
      await Promise.all([refreshData(), profile?.id === playerData.profile.id ? refreshProfile() : Promise.resolve()]);
      setNotice('Saved player presentation changes.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to save player changes.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Portal>
      <div className="player-sheet" role="dialog" aria-modal="true" aria-labelledby="player-sheet-title">
        <button type="button" className="player-sheet__backdrop" aria-label="Close player sheet" onClick={onClose} />
        <aside className="player-sheet__panel">
          <header className="player-sheet__header">
            <div className="player-sheet__identity">
              {playerData.profile.avatarUrl ? (
                <img src={playerData.profile.avatarUrl} alt="" className="player-sheet__avatar-image" />
              ) : (
                <div className="player-sheet__avatar">{getInitials(playerData.inGameName)}</div>
              )}
              <div className="player-sheet__identity-copy">
                <p className="guild-chat__eyebrow">PLAYER PROFILE</p>
                <h3 id="player-sheet-title">{playerData.inGameName}</h3>
                <p>{playerData.profile.displayName}</p>
                <div className="player-sheet__identity-meta">
                  <RoleEmblem role={getPrimaryRole(playerData.profile.roles)} />
                  {playerData.profile.roles
                    .filter((role) => role !== getPrimaryRole(playerData.profile.roles))
                    .map((role) => (
                      <RoleEmblem key={role} role={role} compact />
                    ))}
                  <Badge tone="info">Level {playerData.progressionLevel}</Badge>
                  <Badge tone="default">{playerData.progressionTier}</Badge>
                  {playerData.isManualProgressionOverride ? <Badge tone="warning">Admin Override</Badge> : null}
                  {canSeeModerationState && playerData.profile.chatMuted ? <Badge tone="danger">Chat Muted</Badge> : null}
                </div>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </header>

          <div className="player-sheet__section">
            <div className="panel__header">
              <div>
                <h4 className="panel__title">Guild Identity</h4>
                <p className="panel__description">Role, level, badges, and real attendance standing.</p>
              </div>
            </div>
            <div className="stats-grid stats-grid--compact">
              <div className="stat-card">
                <span className="stat-card__label">Guild Role</span>
                <strong className="stat-card__value">{formatAppRoleLabel(getPrimaryRole(playerData.profile.roles))}</strong>
                <p className="stat-card__hint">Primary rank</p>
              </div>
              <div className="stat-card">
                <span className="stat-card__label">Recorded Signups</span>
                <strong className="stat-card__value">{playerData.totalSignupRecords}</strong>
                <p className="stat-card__hint">Tracked signup history</p>
              </div>
              <div className="stat-card">
                <span className="stat-card__label">Counted Masses</span>
                <strong className="stat-card__value">{playerData.attendanceCount}</strong>
                <p className="stat-card__hint">Real attendance only</p>
              </div>
              <div className="stat-card">
                <span className="stat-card__label">Last Attendance</span>
                <strong className="stat-card__value">{playerData.lastAttendedAt ? formatDateTime(playerData.lastAttendedAt) : 'No record yet'}</strong>
                <p className="stat-card__hint">{playerData.lastAttendedMassTitle ?? 'No counted mass yet'}</p>
              </div>
              <div className="stat-card">
                <span className="stat-card__label">Preferred Loadout</span>
                <strong className="stat-card__value">{topWeapon ? topWeapon.weaponName : 'No usage yet'}</strong>
                <p className="stat-card__hint">{topRole ? `${topRole.roleCategory} x${topRole.usageCount}` : 'No role history yet'}</p>
              </div>
            </div>
          </div>

          {canViewPlayerStats ? (
            <>
              <div className="player-sheet__section">
                <div className="panel__header">
                  <div>
                    <h4 className="panel__title">Weapon & Role History</h4>
                    <p className="panel__description">Based on counted mass attendance, not temporary signups.</p>
                  </div>
                </div>

                <div className="player-sheet__usage-grid">
                  <div className="player-sheet__usage-card">
                    <span className="profile-summary__label">Weapon Usage</span>
                    <div className="usage-list">
                      {playerData.weaponUsage.length > 0 ? (
                        playerData.weaponUsage.slice(0, 8).map((weapon) => (
                          <div key={weapon.weaponName} className="usage-row" style={{ ['--usage-width' as string]: getUsageWidth(weapon.usageCount, maxWeaponUsage) }}>
                            <div className="usage-row__labels">
                              <strong>{weapon.weaponName}</strong>
                              <span>{weapon.usageCount} counted masses</span>
                            </div>
                            <div className="usage-row__track">
                              <span className="usage-row__fill" />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="copy-block">No weapon history recorded yet.</p>
                      )}
                    </div>
                  </div>

                  <div className="player-sheet__usage-card">
                    <span className="profile-summary__label">Role Usage</span>
                    <div className="usage-list">
                      {playerData.roleUsage.length > 0 ? (
                        playerData.roleUsage.slice(0, 8).map((role) => (
                          <div
                            key={role.roleCategory}
                            className="usage-row"
                            style={{ ['--usage-width' as string]: getUsageWidth(role.usageCount, maxRoleUsage) }}
                          >
                            <div className="usage-row__labels">
                              <strong>{role.roleCategory}</strong>
                              <span>{role.usageCount} counted masses</span>
                            </div>
                            <div className="usage-row__track">
                              <span className="usage-row__fill" />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="copy-block">No role history recorded yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="player-sheet__section">
                <div className="panel__header">
                  <div>
                    <h4 className="panel__title">Recent Attendance</h4>
                    <p className="panel__description">Real attendance snapshots locked from the final valid signup state when the mass closed.</p>
                  </div>
                </div>
                <div className="player-sheet__history">
                  {playerData.recentAttendance.length > 0 ? (
                    playerData.recentAttendance.map((entry) => (
                      <Link
                        key={`${entry.eventId}-${entry.startsAt}-${entry.partyNumber}`}
                        to={eventRoutes.detail(entry.eventId)}
                        className="player-sheet__history-item"
                        onClick={onClose}
                      >
                        <strong>{entry.eventTitle}</strong>
                        <span>
                          {entry.weaponName} - {entry.roleCategory} - Party {entry.partyNumber}
                        </span>
                        <span>{formatDateTime(entry.startsAt)}</span>
                      </Link>
                    ))
                  ) : (
                    <p className="copy-block">No counted masses yet.</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="player-sheet__section">
              <p className="copy-block">Player history becomes visible after approval. Discord sign-in alone is not enough to access guild data.</p>
            </div>
          )}

          {canSeeModerationState && playerData.profile.chatMuted ? (
            <div className="player-sheet__section">
              <div className="panel__header">
                <div>
                  <h4 className="panel__title">Moderation Status</h4>
                  <p className="panel__description">Visible only to staff or the affected player.</p>
                </div>
              </div>
              <div className="slot-card__badges">
                <Badge tone="danger">Muted in Guild Chat</Badge>
                {playerData.profile.chatMutedReason ? <Badge tone="default">{playerData.profile.chatMutedReason}</Badge> : null}
              </div>
            </div>
          ) : null}

          {canAdminManagePlayer ? (
            <div className="player-sheet__section">
              <div className="panel__header">
                <div>
                  <h4 className="panel__title">Owner/Admin Controls</h4>
                  <p className="panel__description">Moderation and progression overrides.</p>
                </div>
              </div>

              <div className="form-grid form-grid--two">
                <label className="field">
                  <span className="field__label">Displayed Level Override</span>
                  <input
                    className="field__input"
                    type="number"
                    min={1}
                    value={progressionLevelOverride}
                    onChange={(event) => setProgressionLevelOverride(event.target.value)}
                    placeholder="Automatic from attendance"
                  />
                </label>

                <label className="field player-sheet__toggle-field">
                  <span className="field__label">Guild Chat Muted</span>
                  <button
                    type="button"
                    className={`player-sheet__toggle${chatMuted ? ' player-sheet__toggle--active' : ''}`}
                    onClick={() => setChatMuted((current) => !current)}
                  >
                    {chatMuted ? 'Muted' : 'Active'}
                  </button>
                </label>
              </div>

              <label className="field">
                <span className="field__label">Mute Reason</span>
                <input
                  className="field__input"
                  value={chatMutedReason}
                  onChange={(event) => setChatMutedReason(event.target.value)}
                  placeholder="Optional moderation note"
                />
              </label>

              {notice ? <p className="form-footnote">{notice}</p> : null}
              {error ? <p className="form-footnote form-footnote--error">{error}</p> : null}

              <div className="panel-actions">
                <Button onClick={() => void handleSave()} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Admin Changes'}
                </Button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </Portal>
  );
}
